import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLine } from "./motion-helpers";
import { HERO_TRIPTYCH } from "./products";

const EASE = [0.16, 1, 0.3, 1];

export const Hero = ({ scrollTo }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative z-10 min-h-[100svh] flex flex-col items-center justify-center px-6 pt-28 pb-16"
      data-testid="hero-section"
    >
      <motion.div style={{ opacity: fade }} className="w-full flex flex-col items-center">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15 }}
          className="mb-8 text-[11px] uppercase tracking-[0.5em] text-[#8C8177]"
          data-testid="hero-eyebrow"
        >
          Sparkling Kombucha · Made in India
        </motion.p>

        {/* Headline */}
        <h1 className="text-center font-display font-bold leading-[0.98]">
          <span className="block text-4xl sm:text-6xl md:text-7xl lg:text-8xl text-[#F5EFE6]">
            <MaskedLine delay={0.3}>Not another beverage.</MaskedLine>
          </span>
          <span className="mt-2 block text-4xl sm:text-6xl md:text-7xl lg:text-8xl">
            <MaskedLine delay={0.45}>
              <span className="text-[#D9A441]">Something worth waiting for.</span>
            </MaskedLine>
          </span>
        </h1>

        {/* Supporting copy */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          className="mt-8 max-w-xl text-center text-base sm:text-lg leading-relaxed text-[#CFC6BC]"
          data-testid="hero-supporting-copy"
        >
          Meet DUNC — bold, sparkling kombucha made for the curious.
        </motion.p>

        {/* Bottle triptych */}
        <motion.div
          style={{ y }}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.9, ease: EASE }}
          className="relative mt-12 md:mt-16 grid w-full max-w-5xl grid-cols-3 gap-3 sm:gap-5"
          data-testid="hero-triptych"
        >
          {HERO_TRIPTYCH.map((b, i) => (
            <motion.div
              key={b.src}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="relative overflow-hidden rounded-2xl"
              style={{
                background: `radial-gradient(120% 90% at 50% 15%, ${b.tint}22, transparent 60%), #1a1512`,
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <img
                src={b.src}
                srcSet={b.srcSet}
                sizes="(max-width: 768px) 33vw, 30vw"
                alt={b.alt}
                loading={i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : "auto"}
                decoding="async"
                width="800"
                height="1418"
                className="w-full h-full object-cover"
              />
            </motion.div>
          ))}
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="mt-12 flex flex-col sm:flex-row items-center gap-4"
        >
          <button
            onClick={() => scrollTo("#meet")}
            data-testid="hero-explore-btn"
            className="group inline-flex items-center gap-3 rounded-full border border-white/20 px-8 py-4 text-[12px] font-medium uppercase tracking-[0.28em] text-[#F5EFE6] backdrop-blur-sm transition-colors hover:border-[#D9A441]/60 hover:text-[#D9A441]"
          >
            Explore DUNC
            <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
          </button>
          <button
            type="button"
            onClick={() => scrollTo("#try")}
            data-testid="hero-firstsip-btn"
            className="group inline-flex items-center gap-3 rounded-full bg-[#D9A441] px-8 py-4 text-[12px] font-medium uppercase tracking-[0.28em] text-[#181310] transition-colors hover:bg-[#E7B35A]"
            style={{ boxShadow: "0 0 40px rgba(217,164,65,0.20)" }}
          >
            Try DUNC
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
